import {Injectable} from '@angular/core';
import {
    ECConnectorsConfig,
    ECEconomicsTemplateType,
    ECMeasuresTemplateType,
    ECMember,
    ECMemberParticipationType,
    ECMemberPODRelationType,
    ECMemberState,
    ECPodCoordinates,
    ECRepresentative,
    EnergyCommunityNamedPod,
    Entity,
    EntitySubType,
    Hierarchy,
    NewECMemberRequest,
    NewSecureECRepresentativeRequest,
    PlantStatisticalProfile,
    PodStatisticalProfile,
    UpdateECMemberRequest,
    UpdateECRepresentativeRequest,
    UpdateEnergyCommunityRequest
} from 'smag-commons/cfg/hierarchy';
import {Config as ConnectorConfig, ConfigType as ConnectorConfigType, Device as ConnectorDevice} from 'smag-commons/cfg/connector';
import {
    BooleanResponse,
    ConnectorDeviceInfo,
    ConsumerPodPredictiveModelRequest,
    ConsumerPodStatisticalProfileRequest,
    CustomerConfig,
    EntityMeasureAggregationResponse,
    EntityMeasureAggregationType,
    EntityNetwork,
    EntityNetworkInfo,
    FeaturesConfig,
    IdResponse,
    MeasureAggregationDataSource,
    MeasureTimeAggregationResponse,
    NotificationDetail,
    Permission,
    PermissionAssignment,
    PermissionObjectType,
    PermissionSubjectType,
    ProducerPodStatisticalProfileRequest,
    ProsumerPodPredictiveModelRequest,
    ProsumerPodStatisticalProfileRequest,
    PvPlantPredictiveModelRequest,
    PvPlantStatisticalProfileRequest,
    Result,
    TimeAggregationType,
    User,
    WindPlantPredictiveModelRequest,
    WindPlantStatisticalProfileRequest
} from 'smag-commons/dto/api';
import {
    ECAggregateCommunities,
    ECAggregateEnergies,
    ECAggregateTotals,
    ECComputedCommunityEconomics,
    ECEconomicsAggregatedResponse,
    ECEconomicsMarketResponse,
    ECEconomicsPlantsPods,
    ECEconomicsPlantsResponse,
    ECEconomicsRedistributedMembers,
    ECEconomicsRedistributedResponse,
    ECEconomicsStrategyFeatures,
    ECEconomicsSubjectsSummaryResponse,
    ECEconomicsThirdPartyProducerSummary,
    ECMembersStatistics,
    ECPlantsStatistics,
    ECPlantType,
    ECPodsStatistics
} from 'smag-commons/dto/eco';
import {Observable} from 'rxjs';
import {Config, ConfigType} from 'smag-commons/cfg';
import {Forecast, Measure, Notification, Planning, ResetConnectorPayload, Severity} from 'smag-commons/dto/msg';
import {map} from 'rxjs/operators';
import {isDefined} from '../commons/commons';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {AuthorizedObject, Permissions, TurboObjectWithDates} from './dto';
import {AuthClientService} from 'auth-commons';
import {PlanPayload} from 'smag-commons/dto/msg/cmds';
import * as moment from 'moment';
import {ECMemberDocument, NewEnergyCommunityRequest} from 'smag-commons/cfg/hierarchy/ec';
import {Capabilities as PlanningCapabilities} from 'smag-commons/dto/planning';
import {Capabilities as PredictionCapabilities} from 'smag-commons/dto/prediction';
import {MonthlyValue} from 'smag-commons/cfg/value';
import {MeasureType as MeteoMeasureType} from 'smag-commons/cfg/connector/meteo';
import {ECMembersUploadTemplateType} from 'smag-commons/dto/api/templates';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {SubjectInfo} from 'smag-commons/dto/api/subject-info';
import {YearlyValue} from 'smag-commons/cfg/value/yearly-value';
import {ECEconomicsAdvancedUserSummary, ECEconomicsSummaryResponse, ECSetPodsYearlyValueRequest} from 'smag-commons/dto/eco/community';
import {
    EnergyCommunityPodSpatialCoordinateRequest,
    GSEUploadResponse,
    MassiveSubjectsUploadResponse
} from 'smag-commons/dto/api/massive-upload';

/**
 * Api service that deals with SMAG API backend.
 */
@Injectable()
export class SmagApiService {

    constructor(private readonly clientService: AuthClientService,
                private readonly http: HttpClient) {
    }

    /**
     * Returns a component-version map for the system.
     */
    getVersions(): Observable<{ [component: string]: string }> {
        return this.clientService
            .get('/configs/versions', {params: mkParams(), auth: false});
    }

    /**
     * Retrieves a specific configuration.
     */
    getConfig(cfgType: ConfigType): Observable<Config> {
        return this.clientService
            .get(`/configs/${encodeURIComponent(cfgType)}`, {params: mkParams()});
    }

    /**
     * Retrieves a feature configuration.
     */
    getFeatureConfig(): Observable<FeaturesConfig> {
        return this.clientService
            .get(`/configs/features`, {params: mkParams()});
    }

    /**
     * Retrieves customer-specific configuration.
     */
    getCustomerConfig(entityId?: string): Observable<CustomerConfig> {
        return this.clientService.get(
            isDefined(entityId) ? `/configs/customer/${encodeURIComponent(entityId)}` : '/configs/customer',
            {params: mkParams()});
    }

    /**
     * Retrieves the whole entity hierarchy.
     */
    getHierarchy(): Observable<AuthorizedObject<Hierarchy>> {
        return this.clientService
            .get('/hierarchy', {params: mkParams()});
    }

    /**
     * Renames an entity of the hierarchy.
     */
    renameEntity(id: string, name: string): Promise<void> {
        return this.clientService
            .put(`/hierarchy/rename/${encodeURIComponent(id)}`, undefined, {params: mkParams().set('name', name)})
            .toPromise()
            .then();
    }

    /**
     * Tags an entity of the hierarchy.
     */
    tagEntity(id: string, tags: string[]): Promise<void> {
        let params = mkParams();
        if (tags?.length) {
            params = params.appendAll({tag: tags});
        }
        return this.clientService
            .put(`/hierarchy/tag/${encodeURIComponent(id)}`, undefined, {params})
            .toPromise()
            .then();
    }

    /**
     * Moves an entity of the hierarchy under a new parent and/or before one of its siblings.
     */
    moveEntity(id: string, newParent: string, moveBefore?: string, moveAfter?: string): Promise<void> {
        let params = mkParams();
        if (newParent) {
            params = params.set('new-parent', newParent);
        }
        if (moveBefore) {
            params = params.set('move-before', moveBefore);
        }
        if (moveAfter) {
            params = params.set('move-after', moveAfter);
        }
        return this.clientService
            .put(`/hierarchy/move/${encodeURIComponent(id)}`, undefined, {params})
            .toPromise()
            .then();
    }

    /**
     * Retrieves detailed information about an entity of the hierarchy.
     */
    getEntity(id: string): Observable<TurboObjectWithDates<Entity>> {
        return this.clientService
            .get(`/hierarchy/${encodeURIComponent(id)}`, {params: mkParams()});
    }

    /**
     * Checks whether an entity with the given POD exists.
     */
    checkEntityExistenceByPOD(pod: string): Observable<BooleanResponse> {
        return this.clientService
            .get(`/hierarchy/exists/pod/${encodeURIComponent(pod)}`, {params: mkParams()});
    }

    /**
     * Retrieves the entity whose POD equals the given value.
     */
    findEntityByPOD(pod: string): Observable<IdResponse> {
        return this.clientService
            .get(`/hierarchy/pod/${encodeURIComponent(pod)}`, {params: mkParams()});
    }

    /**
     * Adds a new entity to the hierarchy.
     */
    addEntity(name: string, parent: string, entity: Entity): Promise<IdResponse> {
        let params = mkParams().set('name', name);
        if (parent) {
            params = params.set('parent', parent);
        }
        return this.clientService
            .post('/hierarchy', entity, {params})
            .toPromise()
            .then();
    }

    /**
     * Updates the given entity within the hierarchy.
     */
    updateEntity(id: string, entity: Entity): Promise<void> {
        return this.clientService
            .put(`/hierarchy/${encodeURIComponent(id)}`, entity, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Removed an entity from the hierarchy.
     */
    deleteEntity(id: string): Promise<void> {
        return this.clientService
            .delete(`/hierarchy/${encodeURIComponent(id)}`, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Retrieves all the networks within the hierarchy.
     */
    getNetworks(): Observable<Result<AuthorizedObject<EntityNetwork>>> {
        return this.clientService
            .get('/network', {params: mkParams()});
    }

    /**
     * Retrieves the given network within the hierarchy.
     */
    getNetwork(networkId: string): Observable<AuthorizedObject<EntityNetwork>> {
        return this.clientService
            .get(`/network/${encodeURIComponent(networkId)}`, {params: mkParams()});
    }

    /**
     * Returns the network an entity belongs to. `null` is returned in case no network exists for the
     * given entity.
     */
    getNetworkOfResource(entityId: string): Observable<AuthorizedObject<EntityNetworkInfo>> {
        return this.clientService
            .get(`/network/resource/${encodeURIComponent(entityId)}`, {params: mkParams()});
    }

    /**
     * Retrieves a connector id-connector type for all connectors.
     */
    getConnectorTypes(): Observable<{ [connectorId: string]: ConnectorConfigType }> {
        return this.clientService
            .get('/connectors/types', {params: mkParams()});
    }

    /**
     * Retrieves a connector id-devices map of all connectors' devices.
     */
    getConnectorDevices(): Observable<{ [connector: string]: ConnectorDeviceInfo[] }> {
        return this.clientService
            .get('/connectors/devices', {params: mkParams()});
    }

    /**
     * Retrieves information about a connector device.
     */
    getConnectorDevice(device: string): Observable<ConnectorDeviceInfo> {
        return this.clientService
            .get(`/connectors/devices/${encodeURIComponent(device)}`, {params: mkParams()});
    }

    /**
     * Retrieves the configuration of the connector with the given id.
     * If no id is supplied, an id-connector config map is returned.
     */
    getConnectorConfig(connectorId?: string): Observable<AuthorizedObject<ConnectorConfig<ConnectorDevice>> |
        { [connectorId: string]: AuthorizedObject<ConnectorConfig<ConnectorDevice>> }> {
        if (connectorId) {
            return this.clientService
                .get(`/connectors/${encodeURIComponent(connectorId)}`, {params: mkParams()});
        }
        return this.clientService
            .get('/connectors', {params: mkParams()});
    }

    /**
     * Checks whether the given device belongs to some connector.
     */
    checkConnectorDeviceExistence(device: string): Observable<BooleanResponse> {
        return this.clientService
            .get(`/connectors/exists/device/${encodeURIComponent(device)}`, {params: mkParams()});
    }

    /**
     * Returns the id of the connector the given device belongs to
     * `null` is returned if nothing is found (or the user has not enough permissions).
     */
    findConnectorByDevice(device: string): Observable<IdResponse> {
        return this.clientService
            .get(`/connectors/device/${encodeURIComponent(device)}`, {params: mkParams()});
    }

    /**
     * Returns the meter models supported by the given connector.
     */
    getConnectorSupportedMeters(id: string): Observable<Result<ECConnectorsConfig>> {
        return this.clientService
            .get(`/connectors/${encodeURIComponent(id)}/supported-meters`, {params: mkParams()});
    }

    /**
     * Adds a new connector. If a connector with the given id already exists, its configuration is overwritten.
     */
    saveConnectorConfig(connectorId: string, config: ConnectorConfig<ConnectorDevice>): Promise<void> {
        return this.clientService
            .put(`/connectors/${encodeURIComponent(connectorId)}`, config, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Deletes the configuration of the connector with the given id.
     */
    deleteConnectorConfig(connectorId: string): Promise<void> {
        return this.clientService
            .delete(`/connectors/${encodeURIComponent(connectorId)}`, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Retrieves the permissions on the connector with the given id.
     * If no id is supplied, permissions on "all" connectors are returned.
     */
    getConnectorPermissions(connectorId?: string): Observable<Permissions> {
        if (connectorId) {
            return this.clientService
                .get(`/connectors/permissions/${encodeURIComponent(connectorId)}`, {params: mkParams()});
        }
        return this.clientService
            .get('/connectors/permissions', {params: mkParams()});
    }

    /**
     * Retrieves the permissions for all and only those connectors for which *some* permissions are set
     * (for the current user).
     */
    getAllPermittedConnectors(): Observable<{ [connectorId: string]: Permission[] }> {
        return this.clientService
            .get('/connectors/permitted', {params: mkParams()});
    }

    /**
     * Aggregates values of some measures in time.
     */
    aggregateMeasures(entityId: string, devices: string[], time: number, duration: string, interval: string, aggregation: TimeAggregationType, dataSource: MeasureAggregationDataSource, collapse?: boolean): Observable<MeasureTimeAggregationResponse> {
        const params = mkParams()
            .appendAll({device: devices})
            .set('time', time.toString())
            .set('duration', duration)
            .set('interval', interval)
            .set('aggregation', aggregation)
            .set('data-source', dataSource)
            .set('collapse', collapse ? 'true' : 'false');
        return this.clientService
            .get(`/measure/aggregate/${encodeURIComponent(entityId)}`, {params});
    }

    /**
     * Recursively (transversing the hierarchy) aggregates values of some measures
     * (depending on aggregation type) in time.
     */
    aggregateEntityMeasures(entityId: string, type: EntityMeasureAggregationType, time: number, duration: string, interval: string, timeAggregation: TimeAggregationType, dataSource: MeasureAggregationDataSource, ignoreMissingValues?: boolean, collapse?: boolean): Observable<EntityMeasureAggregationResponse> {
        let params = mkParams()
            .set('time', time.toString())
            .set('duration', duration)
            .set('interval', interval)
            .set('time-aggregation', timeAggregation)
            .set('data-source', dataSource)
            .set('collapse', collapse ? 'true' : 'false');
        if (isDefined(ignoreMissingValues)) {
            params = params.set('ignore-missing-values', ignoreMissingValues.toString());
        }
        return this.clientService
            .get(`/aggregate/${encodeURIComponent(entityId)}/${encodeURIComponent(type)}`, {params});
    }

    /**
     * Retrieves all notifications satisfying the given criteria.
     */
    retrieveNotifications(entityIds?: string[], sources?: string[], services?: string[], statuses?: string[], severities?: Severity[], fromTime?: number, toTime?: number, n?: number): Observable<Result<Notification>> {
        let params = mkParams();
        if (entityIds?.length) {
            params = params.appendAll({'entity-id': entityIds});
        }
        if (sources?.length) {
            params = params.appendAll({source: sources});
        }
        if (services?.length) {
            params = params.appendAll({service: services});
        }
        if (statuses?.length) {
            params = params.appendAll({status: statuses});
        }
        if (severities?.length) {
            params = params.appendAll({severity: severities});
        }
        if (isDefined(fromTime)) {
            params = params.set('from', fromTime.toString());
        }
        if (isDefined(toTime)) {
            params = params.set('to', toTime.toString());
        }
        if (isDefined(n)) {
            params = params.set('n', n.toString());
        }
        return this.clientService
            .get('/notification', {params});
    }

    /**
     * Retrieves details for the notification with the given correlation id.
     */
    retrieveNotificationDetails(correlationId: string): Observable<Result<NotificationDetail>> {
        return this.clientService
            .get(`/notification/${encodeURIComponent(correlationId)}`, {params: mkParams()});
    }

    /**
     * Sends the given planning request to the DSS.
     */
    planNetwork(payload: PlanPayload, source?: string): Promise<IdResponse> {
        let params = mkParams();
        if (source) {
            params = params.set('source', source);
        }
        return this.clientService
            .post('/network/plan', payload, {params})
            .toPromise()
            .then();
    }

    /**
     * Sends the given connector reset request to the DSS.
     */
    resetConnectors(payload: ResetConnectorPayload, source?: string): Promise<IdResponse> {
        let params = mkParams();
        if (source) {
            params = params.set('source', source);
        }
        return this.clientService
            .post('/connectors/reset', payload, {params})
            .toPromise()
            .then();
    }

    /**
     * Retrieves all plannings satisfying the given criteria.
     */
    retrievePlannings(networkIds?: string[], fromTime?: number, toTime?: number, n?: number, data?: boolean): Observable<Result<Planning>> {
        let params = mkParams();
        if (networkIds?.length) {
            params = params.appendAll({'entity-id': networkIds});
        }
        if (isDefined(fromTime)) {
            params = params.set('from', fromTime.toString());
        }
        if (isDefined(toTime)) {
            params = params.set('to', toTime.toString());
        }
        if (isDefined(n)) {
            params = params.set('n', n.toString());
        }
        if (isDefined(data)) {
            params = params.set('data', data.toString());
        }
        return this.clientService
            .get('/planning', {params});
    }

    /**
     * Retrieves a specific planning.
     */
    retrievePlanning(id: string, data?: boolean): Observable<Planning> {
        let params = mkParams();
        if (isDefined(data)) {
            params = params.set('data', data.toString());
        }
        return this.clientService
            .get(`/planning/${encodeURIComponent(id)}`, {params});
    }

    /**
     * Retrieves all forecasts satisfying the given criteria.
     */
    retrieveForecasts(entityIds?: string[], fromTime?: number, toTime?: number, n?: number, data?: boolean): Observable<Result<Forecast>> {
        let params = mkParams();
        if (entityIds?.length) {
            params = params.appendAll({'entity-id': entityIds});
        }
        if (isDefined(fromTime)) {
            params = params.set('from', fromTime.toString());
        }
        if (isDefined(toTime)) {
            params = params.set('to', toTime.toString());
        }
        if (isDefined(n)) {
            params = params.set('n', n.toString());
        }
        if (isDefined(data)) {
            params = params.set('data', data.toString());
        }
        return this.clientService
            .get('/forecast', {params});
    }

    /**
     * Retrieves a specific forecast.
     */
    retrieveForecast(id: string, data?: boolean): Observable<Forecast> {
        let params = mkParams();
        if (isDefined(data)) {
            params = params.set('data', data.toString());
        }
        return this.clientService
            .get(`/forecast/${encodeURIComponent(id)}`, {params});
    }

    /**
     * Computes economics parameters for an Energy Community.
     */
    ecEconomicsAggregated(communityId: string, fromTime: number, toTime: number): Observable<ECEconomicsAggregatedResponse> {
        const params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/aggregated`, {params});
    }

    ecEconomicsMarket(communityId: string, fromTime: number, toTime: number): Observable<ECEconomicsMarketResponse> {
        const params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/market`, {params});
    }

    ecEconomicsPlants(communityId: string, fromTime: number, toTime: number): Observable<ECEconomicsPlantsResponse> {
        const params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/plants`, {params});
    }

    ecEconomicsPlantsPods(communityId: string, fromTime: number, toTime: number, page: number, size: number, search?: string, type?: ECPlantType): Observable<ECEconomicsPlantsPods> {
        let params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString())
            .set('page', page.toString())
            .set('size', size.toString());
        if (search !== '') {
            params = params.set('search', search);
        }
        if (isDefined(type)) {
            params = params.set('type', type);
        }
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/plants/pods`, {params});
    }

    setMonthlyValue(value: MonthlyValue): Promise<void> {
        return this.clientService
            .put(`/economics/value/monthly`, value, {params: mkParams()})
            .toPromise()
            .then();
    }

    setYearlyValue(value: YearlyValue): Promise<void> {
        return this.clientService
            .put(`/economics/value/yearly`, value, {params: mkParams()})
            .toPromise()
            .then();
    }

    setPodsYearlyValue(communityId: string, value: ECSetPodsYearlyValueRequest): Promise<void> {
        return this.clientService
            .put(`/community/${encodeURIComponent(communityId)}/pods/value/yearly`, value, {params: mkParams()})
            .toPromise()
            .then();
    }

    getMonthlyValue(value: MonthlyValue): Observable<MonthlyValue> {
        const params = mkParams()
            .set('name', value.name.toString())
            .set('object-id', value.object_id.toString())
            .set('object-type', value.object_type.toString())
            .set('year', value.year.toString())
            .set('month', value.month.toString());
        return this.clientService
            .get(`/economics/value/monthly`, {params});
    }

    getYearlyValue(value: YearlyValue): Observable<YearlyValue> {
        const params = mkParams()
            .set('name', value.name.toString())
            .set('object-id', value.object_id.toString())
            .set('object-type', value.object_type.toString())
            .set('year', value.year.toString());
        return this.clientService
            .get(`/economics/value/yearly`, {params});
    }

    getConsolidation(networkId: string, year: number, month: number): Observable<BooleanResponse> {
        const params = mkParams()
            .set('year', year.toString())
            .set('month', month.toString());
        return this.clientService
            .get(`/economics/${encodeURIComponent(networkId)}/consolidate`, {params});
    }

    getConsolidationByYear(networkId: string, year: number): Observable<Map<number, boolean>> {
        const params = mkParams();
        return this.clientService
            .get(`/economics/${encodeURIComponent(networkId)}/consolidate/${year}`, {params});
    }

    consolidate(networkId: string, year: number, month: number): Observable<void> {
        const params = mkParams()
            .set('year', year.toString())
            .set('month', month.toString());
        return this.clientService
            .post(`/economics/${encodeURIComponent(networkId)}/consolidate`, undefined, {params});
    }


    ecEconomicsSummaryResponse(communityId: string, fromTime: number, toTime: number, orderBy?: string[]): Observable<ECEconomicsSummaryResponse> {
        let params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        if (orderBy && orderBy.length > 0) {
            params = params.appendAll({'order-by': orderBy});
        }
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/summary`, {params});
    }

    ecEconomicsRedistributed(communityId: string, fromTime: number, toTime: number): Observable<ECEconomicsRedistributedResponse> {
        const params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/redistributed`, {params});
    }

    ecEconomicsRedistributedMembers(communityId: string, fromTime: number, toTime: number,
                                    page: number, size: number): Observable<ECEconomicsRedistributedMembers> {
        const params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString())
            .set('page', page.toString())
            .set('size', size.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/redistributed/members`, {params});
    }

    ecEconomicsSubjectsSummaries(communityId: string, fromTime: number, toTime: number,
                                 page: number, size: number, name: string, orderBy?: string | string[], typeFilter?: string[]): Observable<ECEconomicsSubjectsSummaryResponse> {
        let params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString())
            .set('page', page.toString())
            .set('size', size.toString())
            .set('subject', name);
        if (typeFilter && typeFilter.length > 0) {
            params = params.appendAll({'subject-types': typeFilter});
        }
        if (orderBy && orderBy.length > 0) {
            params = params.appendAll({'order-by': orderBy});
        }
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/subjects`, {params});
    }

    getECAlgorithmFile(communityId: string, fileType: string, year: number, defaultFile: boolean): Observable<[Blob, string]> {
        const params = mkParams()
            .set('year', year.toString())
            .set('default', defaultFile ? 'true' : 'false');
        return this.clientService
            .rawRequest('get',
                `/community/${encodeURIComponent(communityId)}/economics/redistributed/algorithm/${encodeURIComponent(fileType)}`,
                undefined, {params})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    setECAlgorithmFile(communityId: string, fileType: string, year: number, month: number, file: {
        content: Blob,
        filename: string
    }): Promise<void> {
        const formData = new FormData();
        formData.append('file', file.content, file.filename);
        const params = mkParams()
            .set('year', year.toString())
            .set('month', month.toString());
        return this.clientService
            .formRequest('put',
                `/community/${encodeURIComponent(communityId)}/economics/redistributed/algorithm/${encodeURIComponent(fileType)}`,
                formData,
                {params})
            .toPromise()
            .then();
    }

    ecAggregateEconomics(aggregateId: string, tenantIds?: string[]): Observable<ECAggregateTotals> {
        let params = mkParams();
        if (tenantIds && tenantIds.length > 0) {
            params = params.appendAll({'tenant-id': tenantIds.map(id => !id ? 'NULL' : id)});
        }
        return this.clientService
            .get(`/community/aggregate/${encodeURIComponent(aggregateId)}/economics`, {params});
    }

    ecAggregateEconomicsCommunities(aggregateId: string,
                                    page: number, size: number,
                                    fromTime: number, toTime: number,
                                    tenantIds?: string[],
                                    name?: string,
                                    inactive?: boolean,
                                    types?: EntitySubType[],
                                    orderBy?: string[],
                                    economics?: boolean): Observable<ECAggregateCommunities> {
        let params = mkParams()
            .set('page', page.toString())
            .set('size', size.toString())
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        if (tenantIds && tenantIds.length > 0) {
            params = params.appendAll({'tenant-id': tenantIds.map(id => !id ? 'NULL' : id)});
        }
        if (name) {
            params = params.set('name', name);
        }
        if (isDefined(inactive)) {
            params = params.set('inactive', inactive ? 'true' : 'false');
        }
        if (types && types.length > 0) {
            params = params.appendAll({type: types});
        }
        if (orderBy && orderBy.length > 0) {
            params = params.appendAll({'order-by': orderBy});
        }
        if (isDefined(economics)) {
            params = params.set('economics', economics ? 'true' : 'false');
        }
        return this.clientService
            .get(`/community/aggregate/${encodeURIComponent(aggregateId)}/economics/communities`, {params});
    }

    ecAggregateEconomicsEnergies(aggregateId: string,
                                 fromTime: number, toTime: number,
                                 tenantIds?: string[],
                                 name?: string,
                                 inactive?: boolean,
                                 types?: EntitySubType[]): Observable<ECAggregateEnergies> {
        let params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        if (tenantIds && tenantIds.length > 0) {
            params = params.appendAll({'tenant-id': tenantIds.map(id => !id ? 'NULL' : id)});
        }
        if (name) {
            params = params.set('name', name);
        }
        if (isDefined(inactive)) {
            params = params.set('inactive', inactive ? 'true' : 'false');
        }
        if (types && types.length > 0) {
            params = params.appendAll({type: types});
        }
        return this.clientService
            .get(`/community/aggregate/${encodeURIComponent(aggregateId)}/economics/energies`, {params});
    }

    ecEconomicsSummaryThirdParty(communityId: string, fromTime: number, toTime: number): Observable<ECEconomicsThirdPartyProducerSummary> {
        const params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/summary/third-party`, {params});
    }

    ecEconomicsSummaryAdvanced(communityId: string, fromTime: number, toTime: number): Observable<ECEconomicsAdvancedUserSummary> {
        const params = mkParams()
            .set('from', fromTime.toString())
            .set('to', toTime.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/summary/advanced-user`, {params});
    }

    ecEconomicsStatus(communityId: string, year: number): Observable<ECComputedCommunityEconomics> {
        const params = mkParams()
            .set('year', year.toString());
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/economics/status`, {params});
    }

    getEconomicsStrategyFeatures(communityId: string, year: number): Observable<ECEconomicsStrategyFeatures> {
        const params = mkParams().set('year', year.toString());
        return this.clientService.get(`/community/${encodeURIComponent(communityId)}/economics/strategy/features`, {params});
    }

    getEconomicsStrategies(communityId: string): Observable<string[]> {
        const params = mkParams();
        return this.clientService.get(`/community/${encodeURIComponent(communityId)}/economics/strategies`, {params});
    }

    getEconomicsStrategyAlgorithms(communityId: string, year: number): Observable<string[]> {
        const params = mkParams().set('year', year.toString());
        return this.clientService.get(`/community/${encodeURIComponent(communityId)}/economics/strategy/algorithms`, {params});
    }

    /**
     * Retrieves all the members of the given energy community.
     */
    listMembers(networkId: string,
                page: number, size: number,
                withPods: boolean, withDocuments: boolean,
                orTextFields?: boolean,
                firstName?: string, lastName?: string, companyName?: string,
                email?: string,
                pod?: string,
                states?: ECMemberState[],
                types?: ECMemberParticipationType[],
                podRelationTypes?: ECMemberPODRelationType[],
                accountDisabled?: boolean,
                withoutResources?: boolean,
                podWithoutDate?: boolean,
                deactivatedPod?: boolean,
                orderBy?: string[]): Observable<Result<ECMember>> {
        let params = mkParams({
            page: page.toString(), size: size.toString(),
            pods: withPods ? 'true' : 'false',
            documents: withDocuments ? 'true' : 'false'
        });
        if (orTextFields) {
            params = params.set('or-text-fields', orTextFields ? 'true' : 'false');
        }
        if (firstName) {
            params = params.set('first-name', firstName);
        }
        if (lastName) {
            params = params.set('last-name', lastName);
        }
        if (companyName) {
            params = params.set('company-name', companyName);
        }
        if (email) {
            params = params.set('email', email);
        }
        if (pod) {
            params = params.set('pod', pod);
        }
        if (states?.length) {
            params = params.appendAll({state: states});
        }
        if (types?.length) {
            params = params.appendAll({'participation-type': types});
        }
        if (podRelationTypes?.length) {
            params = params.appendAll({'pod-relation-type': podRelationTypes});
        }
        if (isDefined(accountDisabled)) {
            params = params.set('account-disabled', accountDisabled ? 'true' : 'false');
        }
        if (isDefined(withoutResources)) {
            params = params.set('without-resources', withoutResources ? 'true' : 'false');
        }
        if (isDefined(podWithoutDate)) {
            params = params.set('pod-without-date', podWithoutDate ? 'true' : 'false');
        }
        if (isDefined(deactivatedPod)) {
            params = params.set('deactivated-pod', deactivatedPod ? 'true' : 'false');
        }
        if (orderBy?.length) {
            params = params.appendAll({'order-by': orderBy});
        }
        return this.clientService
            .get(`/community/${encodeURIComponent(networkId)}/members`, {params});
    }

    /**
     * Retrieves all the representatives of a single / multiple tenants.
     */
    listRepresentatives(page: number, size: number,
                        orTextFields?: boolean,
                        firstName?: string, lastName?: string, companyName?: string,
                        email?: string,
                        tenantIds?: string[],
                        orderBy?: string[]): Observable<Result<ECRepresentative>> {
        let params = mkParams({
            page: page.toString(), size: size.toString(),
        });
        if (orTextFields) {
            params = params.set('or-text-fields', orTextFields ? 'true' : 'false');
        }
        if (firstName) {
            params = params.set('first-name', firstName);
        }
        if (lastName) {
            params = params.set('last-name', lastName);
        }
        if (companyName) {
            params = params.set('company-name', companyName);
        }
        if (email) {
            params = params.set('email', email);
        }
        if (tenantIds?.length) {
            params = params.appendAll({'tenant-ids': tenantIds.map(id => !id ? 'NULL' : id)});
        }
        if (orderBy?.length) {
            params = params.appendAll({'order-by': orderBy});
        }
        return this.clientService
            .get(`/community/representatives`, {params});
    }

    /**
     * Checks whether an energy community member connected to the given user exists.
     */
    checkMemberExistenceByUser(u3id: string): Observable<BooleanResponse> {
        return this.clientService
            .get(`/community/members/exists/user/${encodeURIComponent(u3id)}`, {params: mkParams()});
    }

    /**
     * Finds an energy community member by connected user.
     * `null` is returned if nothing is found (or the user has not enough permissions).
     */
    findMemberByUser(u3id: string): Observable<ECMember> {
        return this.clientService
            .get(`/community/members/user/${encodeURIComponent(u3id)}`, {params: mkParams()});
    }

    /**
     * Finds a representative given its networkId.
     */
    findRepresentativeByNetwork(networkId: string): Observable<ECRepresentative> {
        return this.clientService
            .get(`/community/representatives/network/${encodeURIComponent(networkId)}`, {params: mkParams()});
    }

    /**
     * Finds a representative given its u3id.
     */
    findRepresentativeByU3id(u3id: string): Observable<ECRepresentative> {
        return this.clientService
            .get(`/community/representatives/u3id/${encodeURIComponent(u3id)}`, {params: mkParams()});
    }

    /**
     * Finds the list of members related to the given POD in some way,
     * and for each member the list of types of relationship with the given POD.
     * An empty map is returned if nothing is found.
     */
    checkMemberExistenceByPOD(pod: string): Observable<{ [memberId: string]: ECMemberPODRelationType[] }> {
        return this.clientService.get(`/community/members/exists/pod/${encodeURIComponent(pod)}`, {params: mkParams()});
    }

    /**
     * Finds the energy community members that have a relation with the given POD.
     * A Result with an empty list is returned if nothing is found (or the user has not enough permissions).
     */
    findMembersByPOD(pod: string): Observable<Result<ECMember>> {
        return this.clientService.get(`/community/members/pod/${encodeURIComponent(pod)}`, {params: mkParams()});
    }

    /**
     * Loads an energy community member.
     */
    loadMember(memberId: string): Observable<ECMember> {
        return this.clientService
            .get(`/community/members/${encodeURIComponent(memberId)}`, {params: mkParams()});
    }

    /**
     * Loads a representative through its member-id.
     */
    loadRepresentative(memberId: string): Observable<ECRepresentative> {
        return this.clientService
            .get(`/community/representatives/${encodeURIComponent(memberId)}`, {params: mkParams()});
    }

    /**
     * Creates a new energy community member.
     */
    createMember(req: NewECMemberRequest): Promise<ECMember> {
        return this.clientService
            .post('/community/members', req, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Creates a new representative member.
     */
    createRepresentative(req: NewSecureECRepresentativeRequest): Promise<ECRepresentative> {
        return this.clientService
            .post('/community/representatives', req, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Updates the given energy community member.
     */
    updateMember(memberId: string, req: UpdateECMemberRequest, deleteResources: boolean): Promise<void> {
        let params = mkParams();
        if (isDefined(deleteResources)) {
            params = params.set('delete-resources', deleteResources ? 'true' : 'false');
        }
        return this.clientService
            .put(`/community/members/${encodeURIComponent(memberId)}`, req, {params})
            .toPromise()
            .then();
    }

    /**
     * Updates the given representative.
     */
    updateRepresentative(memberId: string, req: UpdateECRepresentativeRequest): Promise<void> {
        return this.clientService
            .put(`/community/representatives/${encodeURIComponent(memberId)}`, req, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Deletes the given energy community member.
     */
    deleteMember(memberId: string, deleteResources: boolean): Promise<void> {
        let params = mkParams();
        if (isDefined(deleteResources)) {
            params = params.set('delete-resources', deleteResources ? 'true' : 'false');
        }
        return this.clientService
            .delete(`/community/members/${encodeURIComponent(memberId)}`, {params})
            .toPromise()
            .then();
    }

    /**
     * Deletes the given representative.
     */
    deleteRepresentative(representativeId: string): Promise<void> {
        return this.clientService
            .delete(`/community/representatives/${encodeURIComponent(representativeId)}`, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Retrieves all the documents of a given Energy Community.
     */
    listECDocuments(page: number, size: number,
                    communityId: string,
                    docType?: string,
                    mobileVisible?: boolean,
                    orderBy?: string[]): Observable<Result<ECMemberDocument>> {
        let params = mkParams({
            page: page.toString(), size: size.toString()
        });
        if (docType) {
            params = params.set('type', docType);
        }
        if (mobileVisible) {
            params = params.set('mobile', mobileVisible ? 'true' : 'false');
        }
        if (orderBy?.length) {
            params = params.appendAll({'order-by': orderBy});
        }

        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/document`, {params});
    }

    /**
     * Creates a new document for a specified community.
     * The generated document id is returned.
     */
    createECDocument(communityId: string, file: { content: Blob, filename: string },
                     documentType: string, metadata?: object, expiration?: number): Promise<ECMemberDocument> {
        const formData = new FormData();
        if (file) {
            formData.append('file', file.content, file.filename);
        }
        formData.append('type', documentType);
        if (metadata) {
            formData.append('metadata', JSON.stringify(metadata));
        }
        if (isDefined(expiration)) {
            formData.append('expiration', expiration.toString());
        }
        return this.clientService
            .formRequest('post',
                `/community/${encodeURIComponent(communityId)}/document`,
                formData,
                {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Uploads a document for a community (and/or updates related information).
     * If no file is passed, then the endpoint is only used to update document's information.
     */
    updateECDocument(communityId: string, documentId: string, documentType: string,
                     file?: {
                         content: Blob,
                         filename: string
                     },
                     metadata?: object, expiration?: number): Promise<void> {
        const formData = new FormData();
        if (file) {
            formData.append('file', file.content, file.filename);
        }
        if (documentType) {
            formData.append('type', documentType);
        }
        if (metadata) {
            formData.append('metadata', JSON.stringify(metadata));
        }
        if (isDefined(expiration)) {
            formData.append('expiration', expiration.toString());
        }
        return this.clientService
            .formRequest('put',
                `/community/${encodeURIComponent(communityId)}/document/${encodeURIComponent(documentId)}`,
                formData,
                {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Deletes a document for a community.
     */
    deleteECDocument(communityId: string, documentId: string): Promise<void> {
        return this.clientService
            .delete(`/community/${encodeURIComponent(communityId)}/document/${encodeURIComponent(documentId)}`, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Downloads the document of a community.
     */
    getECDocument(communityId: string, documentId: string): Observable<[Blob, string]> {
        return this.clientService
            .rawRequest('get',
                `/community/${encodeURIComponent(communityId)}/document/${encodeURIComponent(documentId)}`, undefined, {params: mkParams()})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    /**
     * Gets the data of the privacy document of a community, if it exists.
     */
    getPrivacyDocument(communityId: string): Observable<ECMemberDocument> {
        return this.clientService
            .get(`/community/${encodeURIComponent(communityId)}/privacy`, {params: mkParams()});
    }

    /**
     * Creates a new document for a member.
     * The generated document id is returned.
     */
    createMemberDocument(memberId: string, file: { content: Blob, filename: string },
                         documentType: string, metadata?: object, expiration?: number): Promise<ECMemberDocument> {
        const formData = new FormData();
        if (file) {
            formData.append('file', file.content, file.filename);
        }
        formData.append('type', documentType);
        if (metadata) {
            formData.append('metadata', JSON.stringify(metadata));
        }
        if (isDefined(expiration)) {
            formData.append('expiration', expiration.toString());
        }
        return this.clientService
            .formRequest('post',
                `/community/members/${encodeURIComponent(memberId)}/document`,
                formData,
                {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Uploads a document for a member (and/or updates related information).
     * If no file is passed, then the endpoint is only used to update document's information.
     */
    updateMemberDocument(memberId: string, documentId: string, file?: { content: Blob, filename: string },
                         documentType?: string, metadata?: object, expiration?: number): Promise<void> {
        const formData = new FormData();
        if (file) {
            formData.append('file', file.content, file.filename);
        }
        if (documentType) {
            formData.append('type', documentType);
        }
        if (metadata) {
            formData.append('metadata', JSON.stringify(metadata));
        }
        if (isDefined(expiration)) {
            formData.append('expiration', expiration.toString());
        }
        return this.clientService
            .formRequest('put',
                `/community/members/${encodeURIComponent(memberId)}/document/${encodeURIComponent(documentId)}`,
                formData,
                {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Deletes a document for a member.
     */
    deleteMemberDocument(memberId: string, documentId): Promise<void> {
        return this.clientService
            .delete(`/community/members/${encodeURIComponent(memberId)}/document/${encodeURIComponent(documentId)}`, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Downloads the document of a member.
     */
    getMemberDocument(memberId: string, documentId): Observable<[Blob, string]> {
        return this.clientService
            .rawRequest('get',
                `/community/members/${encodeURIComponent(memberId)}/document/${encodeURIComponent(documentId)}`, undefined, {params: mkParams()})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    downloadMeasuresTemplateExcel(resourceId: string,
                                  fromts: number,
                                  tots: number,
                                  timeMinutes: number,
                                  templateType: ECMeasuresTemplateType): Observable<[Blob, string]> {
        const params = mkParams()
            .set('fromts', fromts.toString())
            .set('tots', tots.toString())
            .set('interval-minute', timeMinutes.toString())
            .set('template-type', templateType);
        return this.clientService
            .rawRequest('get',
                `/community/${encodeURIComponent(resourceId)}/templates/measures`,
                undefined, {params})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    uploadMeasuresTemplateExcel(resourceId: string,
                                fromts: number,
                                tots: number,
                                timeMinutes: number,
                                templateType: ECMeasuresTemplateType,
                                file: {
                                    content: Blob,
                                    filename: string
                                }): Promise<void> {
        const params = mkParams()
            .set('fromts', fromts.toString())
            .set('tots', tots.toString())
            .set('interval-minute', timeMinutes.toString())
            .set('template-type', templateType);
        const formData = new FormData();
        formData.append('file', file.content, file.filename);
        return this.clientService
            .formRequest('put',
                `/community/${encodeURIComponent(resourceId)}/templates/measures`,
                formData, {params})
            .toPromise().then();
    }

    uploadGSEMeasures(communityId: string,
                      file: {
                          content: Blob,
                          filename: string
                      }): Promise<GSEUploadResponse> {
        const params = mkParams();
        const formData = new FormData();
        formData.append('file', file.content, file.filename);
        return this.clientService
            .formRequest('put', `/community/${encodeURIComponent(communityId)}/gse/measures`, formData, {params})
            .toPromise().then();
    }

    deleteGseData(communityId: string, year: number, month: number): Promise<void> {
        const params = mkParams()
            .set('year', year.toString())
            .set('month', month.toString());
        return this.clientService
            .delete(`/community/${encodeURIComponent(communityId)}/gse/measures/`, {params})
            .toPromise().then();
    }


    /**
     * Downloads the members export in an Excel file.
     */
    exportMembersToExcel(communityId: string, totalMembers: number, communityName: string): Observable<[Blob, string]> {
        const params = mkParams()
            .set('page', (1).toString())
            .set('size', totalMembers.toString())
            .set('name', communityName);
        return this.clientService
            .rawRequest('get',
                `/community/${encodeURIComponent(communityId)}/export/members`,
                undefined, {params})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    /**
     * Downloads the economics export in an Excel file.
     */
    exportEconomicsToExcel(templateType: ECEconomicsTemplateType, communityId: string, fromTime: number, toTime: number,
                           communityName: string, page?: number, size?: number): Observable<[Blob, string]> {
        let params = mkParams()
            .set('type', templateType)
            .set('from', fromTime.toString())
            .set('to', toTime.toString())
            .set('name', communityName);
        if (page) {
            params = params.set('page', page.toString());
        }
        if (size) {
            params = params.set('size', size.toString());
        }
        return this.clientService
            .rawRequest('get',
                `/community/${encodeURIComponent(communityId)}/export/economics`,
                undefined, {params})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    /**
     * Downloads the measures export in an Excel file.
     */
    exportMeasuresToExcel(entityId: string, templateType: ECMeasuresTemplateType, time: number, duration: string, interval: string): Observable<[Blob, string]> {
        const params = mkParams()
            .set('time', time.toString())
            .set('duration', duration)
            .set('interval', interval)
            .set('template-type', templateType);
        return this.clientService
            .rawRequest('get',
                `/community/${encodeURIComponent(entityId)}/export/measures`,
                undefined, {params})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    /**
     * Sends an invitation to the given energy community member.
     */
    inviteMember(memberId: string): Promise<void> {
        return this.clientService
            .post(`/community/members/invite/${encodeURIComponent(memberId)}`, undefined, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Sends an invitation to the given representative.
     */
    inviteRepresentative(repId: string): Promise<void> {
        return this.clientService
            .post(`/community/representatives/invite/${encodeURIComponent(repId)}`, undefined, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Gets the user connected to an energy community member.
     */
    getMemberUser(memberId: string): Observable<User> {
        return this.clientService
            .get(`/community/members/${encodeURIComponent(memberId)}/user`, {params: mkParams()});
    }

    /**
     * Gets the user connected to an energy community representative.
     */
    getRepresentativeUser(representativeId: string): Observable<User> {
        return this.clientService
            .get(`/community/representatives/${encodeURIComponent(representativeId)}/user`, {params: mkParams()});
    }

    /**
     * Enables the account of a user connected to an energy community member.
     */
    enableMemberUser(memberId: string): Promise<void> {
        return this.clientService
            .put(`/community/members/${encodeURIComponent(memberId)}/enable-user`, undefined, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Disables the account of a user connected to an energy community member.
     */
    disableMemberUser(memberId: string): Promise<void> {
        return this.clientService
            .put(`/community/members/${encodeURIComponent(memberId)}/disable-user`, undefined, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * detach the account of a user connected to an energy community member and deletes it.
     */
    detachMemberUser(memberId: string): Promise<void> {
        return this.clientService
            .put(`/community/members/${encodeURIComponent(memberId)}/delete-user`, undefined, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Enables the account of a user connected to an energy community member.
     */
    enableRepresentativeUser(representativeId: string): Promise<void> {
        return this.clientService
            .put(`/community/representatives/${encodeURIComponent(representativeId)}/enable-user`, undefined, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Disables the account of a user connected to an energy community member.
     */
    disableRepresentativeUser(representativeId: string): Promise<void> {
        return this.clientService
            .put(`/community/representatives/${encodeURIComponent(representativeId)}/disable-user`, undefined, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Forces some {@link MISSING_CHECKS activation checks} for the user connected to an energy community member.
     */
    forceMemberUserChecks(memberId: string, ...checks: string[]): Promise<void> {
        let params = mkParams();
        if (checks?.length) {
            params = params.appendAll({check: checks});
        }
        return this.clientService
            .put(`/community/members/${encodeURIComponent(memberId)}/user-checks`, undefined, {params})
            .toPromise()
            .then();
    }

    /**
     * Forces some {@link MISSING_CHECKS activation checks} for the user connected to a representative.
     */
    forceRepresentativeUserChecks(representativeId: string, ...checks: string[]): Promise<void> {
        let params = mkParams();
        if (checks?.length) {
            params = params.appendAll({check: checks});
        }
        return this.clientService
            .put(`/community/representatives/${encodeURIComponent(representativeId)}/user-checks`, undefined, {params})
            .toPromise()
            .then();
    }

    /**
     * Clears some {@link MISSING_CHECKS activation checks} for the user connected to an energy community member.
     */
    clearMemberUserChecks(memberId: string, ...checks: string[]): Promise<void> {
        let params = mkParams();
        if (checks?.length) {
            params = params.appendAll({check: checks});
        }
        return this.clientService
            .delete(`/community/members/${encodeURIComponent(memberId)}/user-checks`, {params})
            .toPromise()
            .then();
    }

    /**
     * Given a network id and a representative id, add the network to the representative.
     */
    getRepresentativeByNetworkId(networkId: string): Promise<ECRepresentative> {
        const params = mkParams();
        return this.clientService
            .get(`/community/representatives/network/${encodeURIComponent(networkId)}`, {params})
            .toPromise()
            .then();
    }

    /**
     * Given a network id and a representative id, add the network to the representative.
     */
    addNetworkToRepresentative(representativeId: string, networkId: string): Promise<void> {
        const params = mkParams()
            .set('network-id', networkId);
        return this.clientService
            .put(`/community/representatives/${encodeURIComponent(representativeId)}/network`, undefined, {params})
            .toPromise()
            .then();
    }

    /**
     * Given a network id and a representative id, delete the network from the representative.
     */
    deleteNetworkToRepresentative(representativeId: string, networkId: string): Promise<void> {
        const params = mkParams()
            .set('network-id', networkId);
        return this.clientService
            .delete(`/community/representatives/${encodeURIComponent(representativeId)}/network`, {params})
            .toPromise()
            .then();
    }

    /**
     * Retrieves available weather-related measures for an energy community.
     */
    retrieveECWeatherAvailableMeasures(networkId: string): Observable<Result<MeteoMeasureType>> {
        return this.clientService
            .get(`/community/${encodeURIComponent(networkId)}/weather`, {params: mkParams()});
    }

    /**
     * Retrieves values of weather-related measures for an energy community.
     */
    retrieveECWeatherMeasures(networkId: string, measure: MeteoMeasureType, fromTime?: number, toTime?: number, n?: number): Observable<Result<Measure>> {
        let params = mkParams();
        if (isDefined(fromTime)) {
            params = params.set('from', fromTime.toString());
        }
        if (isDefined(toTime)) {
            params = params.set('to', toTime.toString());
        }
        if (isDefined(n)) {
            params = params.set('n', n.toString());
        }
        return this.clientService
            .get(`/community/${encodeURIComponent(networkId)}/weather/${encodeURIComponent(measure)}`, {params});
    }

    /**
     * Computes statistics for the members of the given energy community.
     */
    communityMembersStatistics(networkId: string): Observable<ECMembersStatistics> {
        return this.clientService
            .get(`/community/${encodeURIComponent(networkId)}/members-statistics`, {params: mkParams()});
    }

    /**
     * Computes statistics for the plants of the given energy community.
     */
    communityPlantsStatistics(networkId: string): Observable<ECPlantsStatistics> {
        return this.clientService
            .get(`/community/${encodeURIComponent(networkId)}/plants-statistics`, {params: mkParams()});
    }

    /**
     * Computes statistics for the PODs of the given energy community.
     */
    communityPodsStatistics(networkId: string): Observable<ECPodsStatistics> {
        return this.clientService
            .get(`/community/${encodeURIComponent(networkId)}/pods-statistics`, {params: mkParams()});
    }

    /**
     * Loads the profile for the given user.
     */
    loadUser(u3id: string): Observable<User> {
        return this.clientService
            .get(`/users/${encodeURIComponent(u3id)}`, {params: mkParams()});
    }

    /**
     * Retrieves the permissions for (all) "users".
     */
    getUserPermissions(): Observable<Permissions> {
        return this.clientService
            .get('/users/permissions', {params: mkParams()});
    }

    /**
     * Retrieves all the representative-assigned communities of a given tenant.
     */
    getCommunitiesWithRepresentative(tenantId: string): Observable<Result<string>> {
        let params = mkParams();
        if (tenantId) {
            params = params.set('tenant-id', tenantId);
        }
        return this.clientService
            .get('/community/representatives/tenant/networks', {params});
    }

    /**
     * Returns all the permission assignments for the given subject or object.
     */
    getPermissionAssignments(subject?: { subjectType: PermissionSubjectType, subjectId: string }, object?: {
        objectType: PermissionObjectType,
        objectId: string
    }): Observable<Result<AuthorizedObject<PermissionAssignment>>> {
        let params = mkParams();
        if (subject) {
            params = params.set('subject-type', subject.subjectType);
            if (subject.subjectId) {
                params = params.set('subject', subject.subjectId);
            }
        }
        if (object) {
            params = params.set('object-type', object.objectType);
            if (object.objectId) {
                params = params.set('object', object.objectId);
            }
        }
        return this.clientService
            .get('/permissions', {params});
    }

    /**
     * Updates the given permission assignment.
     */
    updatePermissionAssignment(assignment: PermissionAssignment): Promise<void> {
        return this.clientService
            .put('/permissions', assignment, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Deletes all the permission assignments for the given subject or object.
     */
    deletePermissionAssignment(subjectType: PermissionSubjectType, subjectId: string, objectType: PermissionObjectType, objectId: string): Promise<void> {
        let params = mkParams()
            .set('subject-type', subjectType)
            .set('subject', subjectId)
            .set('object-type', objectType);
        if (objectId) {
            params = params.set('object', objectId);
        }
        return this.clientService
            .delete('/permissions', {params})
            .toPromise()
            .then();
    }

    /**
     * Returns the current "overall" set of permissions.
     */
    getOverallPermissions(): Observable<Permissions> {
        return this.clientService
            .get('/permissions/overall', {params: mkParams()});
    }

    /**
     * Returns planning capabilities.
     */
    getPlanningCapabilities(): Observable<PlanningCapabilities> {
        return this.clientService
            .get('/planning/capabilities', {params: mkParams()});
    }

    /**
     * Returns prediction capabilities.
     */
    getPredictionCapabilities(): Observable<PredictionCapabilities> {
        return this.clientService.get('/forecast/capabilities', {params: mkParams()});
    }

    /**
     * Sends the customer care request.
     */
    sendCustomerCareRequest(communityId: string, request: object): Promise<void> {
        return this.clientService
            .post(`/customer-care/${encodeURIComponent(communityId)}`, request, {params: mkParams()})
            .toPromise()
            .then();
    }

    getPvPlantStatisticalProfile(req: PvPlantStatisticalProfileRequest): Observable<PlantStatisticalProfile> {
        return this.clientService
            .post('/profiles/pv-plant', req, {params: mkParams()});
    }

    getWindPlantStatisticalProfile(req: WindPlantStatisticalProfileRequest): Observable<PlantStatisticalProfile> {
        return this.clientService
            .post('/profiles/wind-plant', req, {params: mkParams()});
    }

    getConsumerPodStatisticalProfile(req: ConsumerPodStatisticalProfileRequest): Observable<PodStatisticalProfile> {
        return this.clientService
            .post('/profiles/pod/consumer', req, {params: mkParams()});
    }

    getProducerPodStatisticalProfile(req: ProducerPodStatisticalProfileRequest): Observable<PodStatisticalProfile> {
        return this.clientService
            .post('/profiles/pod/producer', req, {params: mkParams()});
    }

    getProsumerPodStatisticalProfile(req: ProsumerPodStatisticalProfileRequest): Observable<PodStatisticalProfile> {
        return this.clientService
            .post('/profiles/pod/prosumer', req, {params: mkParams()});
    }

    getProsumerProfile(entityId: string, consumptionTemplateId: string, year?: number, yearlyConsumption?: number): Observable<PodStatisticalProfile> {
        let params = mkParams({
            'consumption-template': consumptionTemplateId
        });
        if (isDefined(year)) {
            params = params.set('year', year.toString());
        }
        if (isDefined(yearlyConsumption)) {
            params = params.set('yearly-consumption', yearlyConsumption.toString());
        }
        return this.clientService
            .get(`/profiles/template/prosumer/${encodeURIComponent(entityId)}`, {params});
    }

    getPvPlantPredictiveModel(req: PvPlantPredictiveModelRequest, preferred = true): Observable<PredictiveModelInfo> {
        let params = mkParams();
        if (isDefined(preferred)) {
            params = params.set('preferred', preferred ? 'true' : 'false');
        }
        return this.clientService
            .post('/forecast/models/pv-plant', req, {params});
    }

    getWindPlantPredictiveModel(req: WindPlantPredictiveModelRequest, preferred = true): Observable<PredictiveModelInfo> {
        let params = mkParams();
        if (isDefined(preferred)) {
            params = params.set('preferred', preferred ? 'true' : 'false');
        }
        return this.clientService
            .post('/forecast/models/wind-plant', req, {params});
    }

    getConsumerPodPredictiveModel(req: ConsumerPodPredictiveModelRequest, preferred = true): Observable<PredictiveModelInfo> {
        let params = mkParams();
        if (isDefined(preferred)) {
            params = params.set('preferred', preferred ? 'true' : 'false');
        }
        return this.clientService
            .post('/forecast/models/pod/consumer', req, {params});
    }

    getProsumerPodPredictiveModel(req: ProsumerPodPredictiveModelRequest, preferred = true): Observable<PredictiveModelInfo> {
        let params = mkParams();
        if (isDefined(preferred)) {
            params = params.set('preferred', preferred ? 'true' : 'false');
        }
        return this.clientService
            .post('/forecast/models/pod/prosumer', req, {params});
    }

    /**
     * Create a new pod resource
     */
    createPod(networkId: string, req: EnergyCommunityNamedPod): Observable<EnergyCommunityNamedPod> {
        return this.clientService
            .post(`/community/${encodeURIComponent(networkId)}/resources/pod`, req, {params: mkParams()});
    }

    /**
     * Update a pod resource
     */
    updatePod(networkId: string, req: EnergyCommunityNamedPod): Observable<EnergyCommunityNamedPod> {
        return this.clientService
            .put(`/community/${encodeURIComponent(networkId)}/resources/pod`, req, {params: mkParams()});
    }

    /**
     * Delete a pod resource
     */
    deletePod(networkId: string, podId: string): Promise<void> {
        return this.clientService
            .delete(`/community/${encodeURIComponent(networkId)}/resources/pod/${encodeURIComponent(podId)}`, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Get a EC pod resource from id
     */
    getECPod(networkId: string, id: string): Observable<EnergyCommunityNamedPod> {
        return this.clientService
            .get(`/community/${encodeURIComponent(networkId)}/resources/pod/${encodeURIComponent(id)}`, {params: mkParams()});
    }


    /**
     * Create an energy community
     */
    createEnergyCommunity(request: NewEnergyCommunityRequest): Promise<IdResponse> {
        return this.clientService
            .post(`/community`, request, {params: mkParams()})
            .toPromise()
            .then(response => response as IdResponse);
    }

    /**
     * Update an energy community
     */
    updateEnergyCommunity(communityId: string, request: UpdateEnergyCommunityRequest): Promise<void> {
        return this.clientService
            .put(`/community/${encodeURIComponent(communityId)}`, request, {params: mkParams()})
            .toPromise()
            .then();
    }

    /**
     * Delete an energy community
     */
    deleteEnergyCommunity(communityId: string): Promise<void> {
        return this.clientService
            .delete(`/community/${encodeURIComponent(communityId)}`, {params: mkParams()})
            .toPromise()
            .then();
    }

    downloadMembersTemplateExcel(selectedUploadMode: ECMembersUploadTemplateType, entityId: string): Observable<[Blob, string]> {
        const params = mkParams()
            .set('template-type', selectedUploadMode);
        return this.clientService
            .rawRequest('get',
                `community/${encodeURIComponent(entityId)}/templates/members`,
                undefined, {params})
            .pipe(map(data => [data.body, extractAttachmentName(data.headers)]));
    }

    uploadMembersTemplateExcel(resourceId: string,
                               selectedUploadMode: ECMembersUploadTemplateType,
                               file: {
                                   content: Blob,
                                   filename: string
                               }): Promise<MassiveSubjectsUploadResponse> {
        const params = mkParams()
            .set('template-type', selectedUploadMode);
        const formData = new FormData();
        formData.append('file', file.content, file.filename);
        return this.clientService
            .formRequest<MassiveSubjectsUploadResponse>('put',
                `/community/${encodeURIComponent(resourceId)}/templates/members`,
                formData, {params})
            .toPromise();
    }


    updatePodsCoordinates(networkId: string,
                          spatialCoordinatesRequest: EnergyCommunityPodSpatialCoordinateRequest[]): Promise<void> {
        return this.clientService
            .post<void>(`/community/${encodeURIComponent(networkId)}/pod-coordinates`,
                spatialCoordinatesRequest,
                {params: mkParams()})
            .toPromise();
    }


    getPodCoordinates(networkId: string): Promise<Result<ECPodCoordinates>> {
        return this.clientService
            .get<Result<ECPodCoordinates>>(`/community/${encodeURIComponent(networkId)}/pod-coordinates`, {params: mkParams()})
            .toPromise();
    }

    /**
     * Given url, check if is valid or not with head request
     * @param url to check validity
     */
    checkPdfValidity(url) {
        return this.http.head(url, {observe: 'response'}).toPromise();
    }

    /**
     * Get subject info for advanced user which cannot operate
     */
    getSubjectInfo(): Observable<SubjectInfo> {
        return this.clientService
            .get<SubjectInfo>(`/community/subject`, {params: mkParams()});
    }

}

function extractAttachmentName(headers: HttpHeaders): string {
    const header = headers.get('Content-Disposition');
    if (!header) {
        return null;
    }
    const matches = /filename="?([^;"]+)"?;?/g.exec(header);
    return matches && matches.length > 1 ? matches[1] : null;
}

function mkParams(params?: { [p: string]: string | string [] }): HttpParams {
    return (new HttpParams({fromObject: params || {}}))
        .set('_ts_', moment().valueOf().toString());
}

const TINY_BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='; // this is a tiny blank image
